import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, FileText, Calendar, Clock, Receipt, Wallet, Shield, HelpCircle, ArrowRight, Briefcase, TrendingUp, FileCheck, ShieldCheck, AlertTriangle, LayoutDashboard, ListChecks, UsersRound } from 'lucide-react';
import { MyRequestsList } from '../components/MyRequestsList';
import { ManagerApprovalInbox } from '../components/ManagerApprovalInbox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESSPortal = () => {
  const { user, isReportingManager } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');   // overview | my_requests | team_approvals
  const [profile, setProfile] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [prof, bal, leaves, tix, kyc] = await Promise.all([
        axios.get(`${API_URL}/ess/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/leave/balances`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/leave/applications`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/helpdesk`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/kyc/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProfile(prof.data);
      setLeaveBalances(bal.data);
      setRecentLeaves(leaves.data.slice(0, 5));
      setTickets(tix.data.slice(0, 5));
      setPendingKyc(kyc.data || []);
    } catch (e) { console.error(e); }
  };

  const QuickAction = ({ icon: Icon, label, desc, path, color, testId }) => (
    <button data-testid={testId} onClick={() => navigate(path)}
      className="flex items-start space-x-3 bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all text-left w-full group">
      <div className={`p-2.5 rounded-lg ${color} shrink-0`}><Icon className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-[#0052FF] transition-colors">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{desc}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#0052FF] mt-1 shrink-0 transition-colors" />
    </button>
  );

  const statusColor = (s) => {
    const map = { approved: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', rejected: 'bg-red-100 text-red-700', open: 'bg-blue-100 text-blue-700', closed: 'bg-slate-100 text-slate-600' };
    return map[s] || 'bg-slate-100 text-slate-600';
  };

  return (
    <MainLayout>
      <div data-testid="ess-portal-page" className="space-y-6 max-w-5xl">
        {/* Greeting */}
        <div className="bg-gradient-to-r from-[#0052FF] to-[#3B82F6] rounded-2xl p-6 text-white">
          <p className="text-sm opacity-80">Welcome back,</p>
          <h1 className="text-2xl font-bold mt-1">{user?.name || 'Employee'}</h1>
          <p className="text-sm opacity-70 mt-1">{user?.email} &middot; {user?.role?.replace(/_/g, ' ')}</p>
        </div>

        {/* Dual-role tabs — Team Approvals appears ONLY when the employee is
            mapped as Reporting Manager for at least one active employee. */}
        <div className="flex flex-wrap gap-2" data-testid="ess-portal-tabs">
          {[
            { key: 'overview', label: 'Overview', icon: LayoutDashboard },
            { key: 'my_requests', label: 'My Requests', icon: ListChecks },
            ...(isReportingManager ? [{ key: 'team_approvals', label: 'My Team Approvals', icon: UsersRound }] : []),
          ].map((t) => (
            <button key={t.key} data-testid={`ess-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                tab === t.key
                  ? 'bg-[#0052FF] text-white border-[#0052FF] shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'my_requests' && (
          <div data-testid="ess-my-requests-tab">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">My Requests — status of everything you've submitted</h2>
            <MyRequestsList />
          </div>
        )}

        {tab === 'team_approvals' && isReportingManager && (
          <div data-testid="ess-team-approvals-tab">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">My Team Approvals — requests awaiting your action</h2>
            <ManagerApprovalInbox embedded />
          </div>
        )}

        {tab === 'overview' && (<>
        {/* KYC Pending banner */}
        {pendingKyc.length > 0 && (
          <button
            onClick={() => navigate('/ess/kyc')}
            data-testid="ess-kyc-banner"
            className="w-full bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center gap-3 hover:bg-amber-100 transition text-left"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {pendingKyc.length} KYC confirmation{pendingKyc.length > 1 ? 's' : ''} pending — please review.
              </p>
              <p className="text-xs text-amber-700">
                {pendingKyc[0].cycle_name} · due {pendingKyc[0].due_date}
              </p>
            </div>
            <span className="px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-semibold">Confirm Now</span>
          </button>
        )}

        {/* Leave Balances */}
        {leaveBalances.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Leave Balance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {leaveBalances.map((lb, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <p className="text-xs text-slate-500 font-medium">{lb.leave_type_code || lb.leave_type_name}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{lb.balance || 0}</p>
                  <p className="text-xs text-slate-400">of {lb.entitled || 0} days</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <QuickAction testId="qa-leave" icon={Calendar} label="Apply Leave" desc="Submit leave application" path="/ess/leave-attendance" color="bg-emerald-50 text-emerald-600" />
            <QuickAction testId="qa-attendance" icon={Clock} label="Attendance & Regularization" desc="View attendance, submit regularization" path="/ess/leave-attendance" color="bg-blue-50 text-blue-600" />
            <QuickAction testId="qa-payslip" icon={FileText} label="My Payslips" desc="View and download payslips" path="/ess/pay-tax" color="bg-purple-50 text-purple-600" />
            <QuickAction testId="qa-tax" icon={TrendingUp} label="Tax & Declarations" desc="Tax projection, investment proof" path="/ess/pay-tax" color="bg-amber-50 text-amber-600" />
            <QuickAction testId="qa-reimbursement" icon={Receipt} label="Expense Claim Sheets" desc="Submit & file expense claims" path="/ess/expense-claims" color="bg-orange-50 text-orange-600" />
            <QuickAction testId="qa-reimb-tracker" icon={Receipt} label="Reimbursements Tracker" desc="View claim status & payment details" path="/ess/reimbursements" color="bg-teal-50 text-teal-600" />
            <QuickAction testId="qa-loan" icon={Wallet} label="Advance Request" desc="Apply for salary advance" path="/ess/loans" color="bg-red-50 text-red-600" />
            <QuickAction testId="qa-documents" icon={FileCheck} label="Documents & FnF" desc="View documents, final settlement" path="/ess/documents" color="bg-indigo-50 text-indigo-600" />
            <QuickAction testId="qa-helpdesk" icon={HelpCircle} label="Helpdesk" desc="Raise query or support ticket" path="/ess/documents" color="bg-pink-50 text-pink-600" />
            <QuickAction testId="qa-user-guide" icon={HelpCircle} label="User Guide" desc="How to use the portal — read or download PDF" path="/ess/user-guide" color="bg-sky-50 text-sky-600" />
            <QuickAction testId="qa-advance-ledger" icon={Wallet} label="Advance & Claim Balance" desc="See your advance/claim settlement and current balance" path="/ess/advance-ledger" color="bg-emerald-50 text-emerald-600" />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Recent Leave Applications</h3>
            {recentLeaves.length > 0 ? (
              <div className="space-y-2">
                {recentLeaves.map((l, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <div><p className="text-sm text-slate-700">{l.leave_type || 'Leave'}</p><p className="text-xs text-slate-400">{l.from_date} to {l.to_date}</p></div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(l.status)}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-4 text-center">No recent leave applications</p>}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">My Helpdesk Tickets</h3>
            {tickets.length > 0 ? (
              <div className="space-y-2">
                {tickets.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <div><p className="text-sm text-slate-700">{t.subject}</p><p className="text-xs text-slate-400">{t.category}</p></div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(t.status)}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-4 text-center">No tickets</p>}
          </div>
        </div>
        </>)}
      </div>
    </MainLayout>
  );
};

export default ESSPortal;
